// docs/34 SX3 — the audio engine: Web Audio, one graph for browser and Electron alike.
//
//   source → variant gain → [pan] → bus (ui | sfx | voice | ambience) → master → limiter → speakers
//   <audio> → media source → track gain → music bus → duck → master        (docs/44: music streams)
//
// * Files are the build's MP3s (manifest `virtual:fk-sfx`, every bank). Bytes are fetched by
//   preload group (`boot` at start, `encounter` when a fight opens, `lazy` on first use);
//   decoding is lazy and the decoded buffers live under a byte budget (least recently used go first)
//   so a thousand variants never sit in memory as PCM at once.
// * A play is scheduled on the AudioContext clock, so cues computed at the start of a playback clip
//   land on their beats; a play whose buffer is still decoding waits up to `maxLateMs`, then drops.
// * Each bank has a cooldown and a voice cap (the oldest stops); loops crossfade on themselves or loop
//   natively, and start at the server clock's position in the file, so players hear the same rain.
// * The whole licensed library ships too: `playFile` / `startLoopFile` play any WAV by its library
//   path. Its manifest (`libraryUrl`) is fetched on first use; each file plays as a one-variant bank
//   (`lib:<bus>:<path>`) at the library's evened loudness, so cooldowns, voice caps and loops behave alike.
// * Music (banks of kind `music`, docs/44) is never decoded whole: a three-minute track would be ~70 MB
//   of PCM. Each track streams through an <audio> element into the graph; `playMusic` crossfades from
//   the track playing, `setMusicDuck` lowers the score under a cinematic's words, and `onMusicEnded`
//   tells the director a piece has finished so it can choose the next.
// * Levels follow `SettingsStore.audio` (master, effects, ambience, music); the page going to the background
//   mutes it when the player asks. The context resumes on the first gesture (browser autoplay rules);
//   Electron allows audio at once.
import manifest, { libraryUrl, type SfxBank, type SfxBus, type SfxLibrary, type SfxLibraryEntry, type SfxManifest, type SfxVariant } from 'virtual:fk-sfx';
import { bandRange, dbToGain, inBand, pickVariant, sliderToGain, variantGain } from './mix';
import { deviceNow, serverNow } from '../time';
import { HALL_LOWPASS_HZ, HALL_RETURN, hallImpulse } from './hall';

export interface PlayOptions {
  /** Variant index (server cues carry theirs); omitted = a local random pick inside `band`. */
  variant?: number;
  band?: number;
  /** Linear multiplier on the bank's level (1 = as mixed). */
  gain?: number;
  /** Playback rate (1 = as recorded); omitted = random in the bank's rate band. */
  rate?: number;
  /** Stereo position −1 (left) .. 1 (right). */
  pan?: number;
  /** docs/92 HM7: how much of it rings in the huge bodies' hall (0 = dry; `hall.ts` `hugeSpace`). */
  space?: number;
  /** Start this long from now (ms). */
  delayMs?: number;
  /** How late a play may start when its buffer was still decoding (ms); later plays drop. */
  maxLateMs?: number;
  /** Groups scheduled plays so a skipped playback can cancel the ones not yet started. */
  tag?: string;
  /** Play when the browser lets audio start, if that is within 20 s (the title's stinger). */
  whenUnlocked?: boolean;
  /** Where it came from, for the test log: a server cue or a local sound. */
  origin?: 'server' | 'local';
}

export interface LoopOptions { variant?: number; band?: number; gain?: number; fadeMs?: number }

/** A library file's own mix: which bus it plays on and its level before `gain` (a bank's `gain_db`). */
export interface FileOptions { bus?: SfxBus; gainDb?: number }

/** Library files sit at the level of a typical effects bank. */
const LIBRARY_GAIN_DB = -11;

export interface Levels { master: number; sfx: number; ambience: number; music?: number; muted?: boolean }

export interface MusicOptions {
  /** The track (variant) of the music bank; omitted = a random one, never the one just played. */
  variant?: number;
  /** Crossfade from what is playing (ms). */
  fadeMs?: number;
  /** Linear multiplier on the track's level. */
  gain?: number;
  /** Loop the track rather than end (and fire `onMusicEnded`). */
  loop?: boolean;
  /** Start this many seconds in (docs/116: a tune the room has been hearing for a while). */
  startAt?: number;
}

interface Track { key: string; variant: number; el: HTMLAudioElement; node: MediaElementAudioSourceNode; gain: GainNode; target: number; stopped: boolean; started: boolean; objectUrl?: string }

interface Voice { key: string; src: AudioBufferSourceNode; gain: GainNode; startAt: number; endAt: number; tag?: string; stopped: boolean }

interface Loop {
  key: string; variant: number; gain: GainNode; target: number;
  sources: AudioBufferSourceNode[]; timer?: ReturnType<typeof setTimeout>; stopped: boolean;
}

export interface PlayRecord { key: string; variant: number; gain: number; rate: number; delayMs: number; at: number; origin: 'server' | 'local'; dropped?: string }

/**
 * The stretch of a decoded master a variant plays, in buffer seconds (docs/44: a bank's trim and cap
 * are playback, not processing): where it starts, how long it runs, and the fade-out it ends on.
 */
export function region(v: Pick<SfxVariant, 'frames' | 'rate'> & Partial<Pick<SfxVariant, 'startMs' | 'fadeMs'>>, bufDuration: number): { offset: number; duration: number; fade: number } {
  const offset = Math.min(bufDuration, Math.max(0, (v.startMs ?? 0) / 1000));
  const duration = Math.max(0, Math.min(bufDuration - offset, v.frames / v.rate));
  return { offset, duration, fade: Math.min(duration, Math.max(0, (v.fadeMs ?? 0) / 1000)) };
}

const DECODED_BUDGET = 128 * 1024 * 1024;
const MAX_VOICES = 48;
const FETCH_CONCURRENCY = 6;
/** Preloads share at most this many of the pool, so the art and data a screen waits on never queue behind them. */
const PRELOAD_CONCURRENCY = 2;
/**
 * A preload warms this many takes of each band (banks have no cap on their size; docs/43 SC9); a
 * local play prefers a take that is ready and warms another, so the rest arrive as the game is played.
 */
const PRELOAD_PER_BAND = 3;

type Ctx = AudioContext;

export class AudioEngine {
  readonly manifest: SfxManifest;
  private ctx?: Ctx;
  private master?: GainNode;
  private buses = new Map<SfxBus, GainNode>();
  private bytes = new Map<string, Promise<ArrayBuffer | undefined>>();
  private decoded = new Map<string, AudioBuffer>();
  private decoding = new Map<string, Promise<AudioBuffer | undefined>>();
  private decodedBytes = 0;
  private voices: Voice[] = [];
  private lastStart = new Map<string, number>();
  private lastVariant = new Map<string, number>();
  private loops = new Map<string, Loop>();
  private levels: Levels = { master: 80, sfx: 80, ambience: 70, music: 60 };
  private duck?: GainNode;
  private duckAmount = 0;
  private track?: Track;
  private lastTrack = new Map<string, number>();
  private musicEnded = new Set<(t: { key: string; variant: number }) => void>();
  private hidden = false;
  private muteInBackground = true;
  private fetchQueue: Array<((asPreload: boolean) => void) & { url: string; urgent: boolean }> = [];
  private fetching = 0;
  private fetchingPreload = 0;
  private pendingUnlock: Array<{ key: string; opts: PlayOptions; at: number }> = [];
  /** The last plays, newest last (e2e reads it through `window.__fkSfx`). */
  readonly history: PlayRecord[] = [];
  rand: () => number = Math.random;
  /** Where a sound's bytes come from (the web build reads Cache Storage first, docs/44 MM-cache). */
  loadBytes: (url: string) => Promise<ArrayBuffer | undefined> = (url) => fetch(url).then((r) => (r.ok ? r.arrayBuffer() : undefined)).catch(() => undefined);
  /** A local source for a cached music track, when there is one (else it streams from its URL). */
  musicSrc?: (url: string) => Promise<string | undefined>;

  private library?: Promise<Readonly<Record<string, SfxLibraryEntry>>>;
  private libraryFiles?: Readonly<Record<string, SfxLibraryEntry>>;
  private fileBanks = new Map<string, SfxBank>();
  /** docs/92 HM7: the hall's input, built the first time a sound is sent to it. */
  private hallIn?: AudioNode;
  private pendingFileLoops = new Map<string, object>();

  constructor(m: SfxManifest = manifest, private readonly libUrl: string = libraryUrl) { this.manifest = m; }

  // ---- context ---------------------------------------------------------------------------------

  /** Creates the context and graph (idempotent); undefined where there is no Web Audio (tests, SSR). */
  context(): Ctx | undefined {
    if (this.ctx) return this.ctx;
    const C = (globalThis as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext }).AudioContext
      ?? (globalThis as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!C) return undefined;
    let ctx: Ctx;
    try { ctx = new C({ latencyHint: 'interactive', sampleRate: 44_100 }); } catch { ctx = new C({ latencyHint: 'interactive' }); }
    this.ctx = ctx;
    const limiter = ctx.createDynamicsCompressor();
    limiter.threshold.value = -6; limiter.knee.value = 6; limiter.ratio.value = 12; limiter.attack.value = 0.003; limiter.release.value = 0.12;
    limiter.connect(ctx.destination);
    this.master = ctx.createGain();
    this.master.connect(limiter);
    for (const b of ['ui', 'sfx', 'voice', 'ambience'] as const) { const g = ctx.createGain(); g.connect(this.master); this.buses.set(b, g); }
    // the score sits on its own bus, through a duck the cinematics lower under their words
    this.duck = ctx.createGain();
    this.duck.gain.value = 1 - this.duckAmount;
    this.duck.connect(this.master);
    const music = ctx.createGain(); music.connect(this.duck); this.buses.set('music', music);
    this.applyLevels(0);
    if (typeof window !== 'undefined') {
      const unlock = () => { void this.resume(); };
      for (const ev of ['pointerdown', 'keydown', 'touchstart'] as const) window.addEventListener(ev, unlock, { capture: true, passive: true });
      document.addEventListener('visibilitychange', () => { this.hidden = document.visibilityState === 'hidden'; this.applyLevels(0.25); });
    }
    ctx.onstatechange = () => { if (ctx.state === 'running') this.flushUnlocked(); };
    return ctx;
  }

  /** Resumes a suspended context (call from a user gesture). */
  async resume(): Promise<void> {
    const ctx = this.context();
    if (!ctx || ctx.state === 'running') return;
    try { await ctx.resume(); } catch { /* not yet allowed */ }
    if ((ctx.state as AudioContextState) === 'running') this.flushUnlocked();
  }

  get running(): boolean { return this.ctx?.state === 'running'; }

  private flushUnlocked(): void {
    const now = deviceNow();
    const due = this.pendingUnlock.filter((p) => now - p.at < 20_000);
    this.pendingUnlock = [];
    for (const p of due) this.play(p.key, { ...p.opts, whenUnlocked: false });
    // a track the browser would not start before a gesture starts now
    const t = this.track;
    if (t && !t.stopped && !t.started) this.startTrack(t);
  }

  setLevels(levels: Levels, muteInBackground = true): void {
    this.levels = levels; this.muteInBackground = muteInBackground;
    this.applyLevels(0.08);
  }

  private applyLevels(rampS: number): void {
    const ctx = this.ctx;
    if (!ctx || !this.master) return;
    const quiet = this.levels.muted || (this.hidden && this.muteInBackground);
    const t = ctx.currentTime;
    const set = (g: GainNode, v: number) => { g.gain.cancelScheduledValues(t); g.gain.setTargetAtTime(v, t, Math.max(0.001, rampS / 3)); };
    set(this.master, quiet ? 0 : sliderToGain(this.levels.master));
    const fx = sliderToGain(this.levels.sfx);
    set(this.buses.get('ui')!, fx);
    set(this.buses.get('sfx')!, fx);
    set(this.buses.get('voice')!, fx);
    set(this.buses.get('ambience')!, sliderToGain(this.levels.ambience));
    set(this.buses.get('music')!, sliderToGain(this.levels.music ?? 60));
  }

  // ---- loading ---------------------------------------------------------------------------------

  bank(key: string): SfxBank | undefined { return this.manifest.banks[key] ?? this.fileBanks.get(key); }

  // ---- the library -------------------------------------------------------------------------------

  /** The library manifest, fetched once (an empty library when it cannot be had). */
  loadLibrary(): Promise<Readonly<Record<string, SfxLibraryEntry>>> {
    this.library ??= fetch(this.libUrl)
      .then((r): Promise<SfxLibrary> | SfxLibrary => (r.ok ? r.json() as Promise<SfxLibrary> : { files: {} }))
      .catch((): SfxLibrary => ({ files: {} }))
      .then((l: SfxLibrary) => {
        const base = new URL(this.libUrl, globalThis.location?.href ?? 'http://localhost/');
        // entry urls are relative to the manifest
        const files: Record<string, SfxLibraryEntry> = Object.fromEntries(Object.entries(l.files).map(([k, e]) => [k, { ...e, url: new URL(e.url, base).href }]));
        this.libraryFiles = files;
        return files;
      });
    return this.library;
  }

  /** A library file's entry, once the manifest has loaded (undefined before, or for an unknown path). */
  libraryEntry(path: string): SfxLibraryEntry | undefined { return this.libraryFiles?.[path]; }

  /** Every file the build ships: each bank's takes, the music and the whole library (the web download). */
  async allUrls(): Promise<string[]> {
    const urls = Object.values(this.manifest.banks).flatMap((b) => b.variants.map((v) => v.url));
    return [...urls, ...Object.values(await this.loadLibrary()).map((e) => e.url)];
  }

  /** Every library path (natural order). */
  libraryPaths(): Promise<string[]> { return this.loadLibrary().then((f) => Object.keys(f)); }

  /** The one-variant bank a library file plays through, made on first use. */
  private fileBank(path: string, o: FileOptions): string | undefined {
    const e = this.libraryEntry(path);
    if (!e) return undefined;
    const bus = o.bus ?? 'sfx';
    const gainDb = o.gainDb ?? LIBRARY_GAIN_DB;
    const key = `lib:${bus}:${gainDb}:${path}`;
    if (!this.fileBanks.has(key)) {
      this.fileBanks.set(key, {
        bus, kind: 'oneshot', loop: 'crossfade', crossfadeMs: 2000, channels: e.channels, preload: 'lazy', gainDb,
        gainBand: [10_000, 10_000], rateBand: [10_000, 10_000], cooldownMs: 40, maxVoices: 4,
        bands: [{ name: 'all', start: 0, count: 1 }],
        variants: [{ url: e.url, ms: e.ms, frames: e.frames, rate: e.rate, levelDb: e.levelDb, startMs: 0, fadeMs: 0 }],
      });
    }
    return key;
  }

  /** Plays a library file by its path (after the manifest loads, so the first play may wait on it). */
  playFile(path: string, opts: PlayOptions & FileOptions = {}): boolean {
    if (this.libraryFiles) {
      const key = this.fileBank(path, opts);
      if (key) return this.play(key, { ...opts, variant: 0 });
      this.record({ key: `lib:${path}`, variant: -1, gain: 0, rate: 1, delayMs: opts.delayMs ?? 0, at: deviceNow(), origin: opts.origin ?? 'local', dropped: 'unknown file' });
      return false;
    }
    const asked = deviceNow();
    void this.loadLibrary().then(() => {
      // keep the asked-for moment: the wait for the manifest eats into the delay
      const waited = deviceNow() - asked;
      if (this.libraryFiles) this.playFile(path, { ...opts, delayMs: Math.max(0, (opts.delayMs ?? 0) - waited) });
    });
    return true;
  }

  /** Starts loop `id` on a library file (see `startLoop`). */
  startLoopFile(id: string, path: string, opts: LoopOptions & FileOptions = {}): void {
    const run = () => {
      const key = this.fileBank(path, { bus: opts.bus ?? 'ambience', gainDb: opts.gainDb });
      if (key) this.startLoop(id, key, { ...opts, variant: 0 });
    };
    if (this.libraryFiles) { run(); return; }
    // a loop stopped (or restarted) while the manifest loads never starts
    const token = {};
    this.pendingFileLoops.set(id, token);
    void this.loadLibrary().then(() => {
      if (this.pendingFileLoops.get(id) !== token) return;
      this.pendingFileLoops.delete(id);
      run();
    });
  }

  /** Fetches (and with `decode`, decodes) these library files ahead of need. */
  preloadFiles(paths: readonly string[], decode = false): Promise<void> {
    return this.loadLibrary().then(() => this.preload(paths.map((p) => this.fileBank(p, {})).filter((k): k is string => !!k), decode));
  }

  /**
   * Fetches a file through a small pool; `urgent` (a sound about to play) jumps the queue, and
   * preloads never hold more than PRELOAD_CONCURRENCY of the pool.
   */
  private fetchBytes(url: string, urgent = false): Promise<ArrayBuffer | undefined> {
    let p = this.bytes.get(url);
    if (p) { if (urgent) this.hurry(url); return p; }
    p = new Promise<ArrayBuffer | undefined>((resolve) => {
      const job = Object.assign((asPreload: boolean) => {
        this.fetching++;
        if (asPreload) this.fetchingPreload++;
        this.loadBytes(url).catch(() => undefined).then((b) => {
          this.fetching--;
          if (asPreload) this.fetchingPreload--;
          resolve(b ?? undefined);
          this.pump();
        });
      }, { url, urgent });
      if (urgent) this.fetchQueue.unshift(job); else this.fetchQueue.push(job);
      this.pump();
    });
    this.bytes.set(url, p);
    return p;
  }

  /** Starts queued fetches while the pool has room: urgent ones first, preloads within their share. */
  private pump(): void {
    while (this.fetching < FETCH_CONCURRENCY && this.fetchQueue.length) {
      const i = this.fetchQueue.findIndex((j) => j.urgent || this.fetchingPreload < PRELOAD_CONCURRENCY);
      if (i < 0) return;
      const [job] = this.fetchQueue.splice(i, 1);
      job!(!job!.urgent);
    }
  }

  /** Moves a queued fetch to the front. */
  private hurry(url: string): void {
    const i = this.fetchQueue.findIndex((j) => j.url === url);
    if (i >= 0) { const [job] = this.fetchQueue.splice(i, 1); job!.urgent = true; this.fetchQueue.unshift(job!); this.pump(); }
  }

  /**
   * Fetches (and for `decode`, decodes) a few takes of each band of the given banks, at low
   * priority: the band's first (a generated bank lists its best match first) and a random few.
   */
  preload(keys: readonly string[], decode = false): Promise<void> {
    const jobs: Promise<unknown>[] = [];
    for (const key of keys) {
      const b = this.bank(key);
      if (!b || b.kind === 'music') continue;   // a score streams when it plays
      for (let band = 0; band < Math.max(1, b.bands.length); band++) {
        for (const i of this.sample(b, band, PRELOAD_PER_BAND)) jobs.push(decode ? this.buffer(key, i, false) : this.fetchBytes(b.variants[i]!.url));
      }
    }
    return Promise.all(jobs).then(() => undefined);
  }

  /** Up to `n` variants of a band: its first and random others. */
  private sample(b: SfxBank, band: number, n: number): number[] {
    const [start, count] = bandRange(b, b.bands.length ? band : undefined);
    if (count <= n) return Array.from({ length: count }, (_, i) => start + i);
    const out = new Set([start]);
    while (out.size < n) out.add(start + Math.floor(this.rand() * count));
    return [...out];
  }

  /** A local play's take: one already decoded in the band if any (not the last one played), else any. */
  private pickReady(key: string, b: SfxBank, band: number | undefined): number {
    const [start, count] = bandRange(b, band);
    const last = this.lastVariant.get(key);
    const ready: number[] = [];
    for (let i = start; i < start + count; i++) if (i !== last && this.decoded.has(b.variants[i]!.url)) ready.push(i);
    const v = ready.length ? ready[Math.floor(this.rand() * ready.length)]! : pickVariant(b, this.rand, band, last);
    // and warm another take of the band for next time, so variety grows as the game is played
    if (count > 1) {
      const w = start + Math.floor(this.rand() * count);
      const url = b.variants[w]!.url;
      if (!this.decoded.has(url) && !this.decoding.has(url)) void this.buffer(key, w, false);
    }
    return v;
  }

  /** Every bank of a preload group fetched; at `boot` the short ones (clicks, ticks) are decoded too. */
  preloadGroup(group: 'boot' | 'encounter'): Promise<void> {
    const banks = Object.entries(this.manifest.banks).filter(([, b]) => b.preload === group);
    const short = (b: SfxBank) => b.variants.reduce((n, v) => n + v.ms, 0) / Math.max(1, b.variants.length) <= 1200;
    return Promise.all([
      this.preload(banks.filter(([, b]) => group === 'boot' && short(b)).map(([k]) => k), true),
      this.preload(banks.filter(([, b]) => group !== 'boot' || !short(b)).map(([k]) => k)),
    ]).then(() => undefined);
  }

  /** The decoded buffer of one variant (decoding it if needed). */
  buffer(key: string, variant: number, urgent = true): Promise<AudioBuffer | undefined> {
    const b = this.bank(key);
    const v = b?.variants[variant];
    const ctx = this.context();
    if (!b || !v || !ctx || b.kind === 'music') return Promise.resolve(undefined);
    const hit = this.decoded.get(v.url);
    if (hit) { this.decoded.delete(v.url); this.decoded.set(v.url, hit); return Promise.resolve(hit); }
    let p = this.decoding.get(v.url);
    if (p) return p;
    p = this.fetchBytes(v.url, urgent).then(async (bytes) => {
      if (!bytes) return undefined;
      try {
        const buf = await ctx.decodeAudioData(bytes.slice(0));
        this.decoded.set(v.url, buf);
        this.decodedBytes += buf.length * buf.numberOfChannels * 4;
        this.evict();
        return buf;
      } catch { return undefined; } finally { this.decoding.delete(v.url); }
    });
    this.decoding.set(v.url, p);
    return p;
  }

  /** Decodes ahead of time a variant a server cue will play shortly. */
  prepare(key: string, variant: number): void { void this.buffer(key, variant); }

  private evict(): void {
    if (this.decodedBytes <= DECODED_BUDGET) return;
    const busy = new Set(this.voices.filter((v) => !v.stopped).map((v) => v.src.buffer));
    for (const l of this.loops.values()) for (const s of l.sources) busy.add(s.buffer);
    for (const [url, buf] of this.decoded) {
      if (this.decodedBytes <= DECODED_BUDGET * 0.8) break;
      if (busy.has(buf)) continue;
      this.decoded.delete(url);
      this.decodedBytes -= buf.length * buf.numberOfChannels * 4;
    }
  }

  // ---- one-shots -------------------------------------------------------------------------------

  private record(r: PlayRecord): void { this.history.push(r); if (this.history.length > 400) this.history.splice(0, this.history.length - 400); }

  /** Plays a variant of a bank. Returns false when it was dropped (unknown bank, cooldown, too late, no audio). */
  play(key: string, opts: PlayOptions = {}): boolean {
    const b = this.bank(key);
    const origin = opts.origin ?? 'local';
    if (!b) { this.record({ key, variant: -1, gain: 0, rate: 1, delayMs: opts.delayMs ?? 0, at: deviceNow(), origin, dropped: 'unknown bank' }); return false; }
    if (b.kind === 'music') return this.playMusic(key, { variant: opts.variant, gain: opts.gain });
    const variant = opts.variant !== undefined
      ? Math.max(0, Math.min(b.variants.length - 1, opts.variant))
      : this.pickReady(key, b, opts.band);
    this.lastVariant.set(key, variant);
    const rate = opts.rate ?? inBand(b.rateBand, this.rand);
    const gain = (opts.gain ?? inBand(b.gainBand, this.rand)) * variantGain(b, variant);
    const rec: PlayRecord = { key, variant, gain, rate, delayMs: opts.delayMs ?? 0, at: deviceNow(), origin };
    const ctx = this.context();
    if (!ctx) { this.record({ ...rec, dropped: 'no audio' }); return false; }
    if (ctx.state !== 'running' && opts.whenUnlocked) { this.pendingUnlock.push({ key, opts: { ...opts, variant }, at: deviceNow() }); this.record({ ...rec, dropped: 'waiting for a gesture' }); return true; }
    const startAt = ctx.currentTime + Math.max(0, opts.delayMs ?? 0) / 1000;
    const last = this.lastStart.get(key);
    if (last !== undefined && Math.abs(startAt - last) * 1000 < b.cooldownMs) { this.record({ ...rec, dropped: 'cooldown' }); return false; }
    this.lastStart.set(key, startAt);
    this.record(rec);
    const buf = this.decoded.get(b.variants[variant]!.url);
    if (buf) { this.start(key, b, variant, buf, startAt, gain, rate, opts); return true; }
    const maxLate = (opts.maxLateMs ?? (b.bus === 'ui' ? 120 : 250)) / 1000;
    void this.buffer(key, variant).then((ready) => {
      if (!ready || !this.ctx) return;
      const now = this.ctx.currentTime;
      if (now - startAt > maxLate) { rec.dropped = 'late'; return; }
      this.start(key, b, variant, ready, Math.max(now, startAt), gain, rate, opts);
    });
    return true;
  }

  private start(key: string, b: SfxBank, variant: number, buf: AudioBuffer, at: number, gain: number, rate: number, opts: PlayOptions): void {
    const ctx = this.ctx!;
    // the bank's voice cap, then the whole mix's: the oldest gives way
    const mine = this.voices.filter((v) => v.key === key && !v.stopped && v.endAt > ctx.currentTime);
    if (mine.length >= b.maxVoices) this.stopVoice(mine[0]!, 0.03);
    const live = this.voices.filter((v) => !v.stopped && v.endAt > ctx.currentTime);
    if (live.length >= MAX_VOICES) this.stopVoice(live[0]!, 0.03);
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.playbackRate.value = Math.max(0.25, Math.min(4, rate));
    const g = ctx.createGain();
    g.gain.value = gain;
    let tail: AudioNode = g;
    src.connect(g);
    if (opts.pan !== undefined && opts.pan !== 0 && ctx.createStereoPanner) {
      const p = ctx.createStereoPanner(); p.pan.value = Math.max(-1, Math.min(1, opts.pan)); g.connect(p); tail = p;
    }
    tail.connect(this.buses.get(b.bus) ?? this.master!);
    // docs/92 HM7: a huge body's sound rings on in the hall behind the dry hit
    let send: GainNode | undefined;
    const hall = opts.space && opts.space > 0 ? this.hall(b.bus) : undefined;
    if (hall) { send = ctx.createGain(); send.gain.value = Math.min(1, opts.space!); tail.connect(send); send.connect(hall); }
    // the bank's stretch of the master: from its trim's lead, for its played length, fading out at the end
    const r = region(b.variants[variant]!, buf.duration);
    const speed = src.playbackRate.value;
    src.start(at, r.offset, r.duration);
    if (r.fade > 0) {
      g.gain.setValueAtTime(gain, at + (r.duration - r.fade) / speed);
      g.gain.linearRampToValueAtTime(0, at + r.duration / speed);
    }
    const voice: Voice = { key, src, gain: g, startAt: at, endAt: at + r.duration / speed, tag: opts.tag, stopped: false };
    this.voices.push(voice);
    src.onended = () => { voice.stopped = true; this.voices = this.voices.filter((v) => v !== voice); try { tail.disconnect(); send?.disconnect(); } catch { /* gone */ } };
  }

  /**
   * docs/92 HM7: the huge bodies' hall — one convolver on a made impulse, darkened, returning into
   * the bus the sound plays on (so its slider and mute reach the tail). Undefined where Web Audio
   * has no convolver.
   */
  private hall(bus: string): AudioNode | undefined {
    const ctx = this.ctx;
    if (!ctx || !ctx.createConvolver) return undefined;
    if (!this.hallIn) {
      try {
        const conv = ctx.createConvolver();
        const ir = hallImpulse(ctx.sampleRate);
        const buf = ctx.createBuffer(2, ir[0]!.length, ctx.sampleRate);
        ir.forEach((d, ch) => buf.getChannelData(ch).set(d));
        conv.normalize = true;
        conv.buffer = buf;
        const dark = ctx.createBiquadFilter();
        dark.type = 'lowpass'; dark.frequency.value = HALL_LOWPASS_HZ;
        const ret = ctx.createGain();
        ret.gain.value = HALL_RETURN;
        conv.connect(dark); dark.connect(ret);
        ret.connect(this.buses.get(bus === 'voice' ? 'voice' : 'sfx') ?? this.master!);
        this.hallIn = conv;
      } catch { return undefined; }
    }
    return this.hallIn;
  }

  private stopVoice(v: Voice, fadeS: number): void {
    if (v.stopped || !this.ctx) return;
    v.stopped = true;
    const t = Math.max(this.ctx.currentTime, v.startAt);
    try { v.gain.gain.setTargetAtTime(0, t, fadeS / 3); v.src.stop(t + fadeS); } catch { /* already stopped */ }
  }

  /** Cancels plays with `tag` that have not started yet (a skipped playback). */
  cancel(tag: string): void {
    const now = this.ctx?.currentTime ?? 0;
    for (const v of this.voices) if (v.tag === tag && v.startAt > now) this.stopVoice(v, 0.001);
  }

  // ---- loops -----------------------------------------------------------------------------------

  /** Starts (or retunes) loop `id` on a bank; idempotent for the same bank and variant. */
  startLoop(id: string, key: string, opts: LoopOptions = {}): void {
    this.pendingFileLoops.delete(id);
    const b = this.bank(key);
    const ctx = this.context();
    if (!b || !ctx || !b.variants.length) return;
    const [start, count] = bandRange(b, opts.band);
    const variant = opts.variant !== undefined ? opts.variant : start + Math.floor(this.rand() * count);
    const target = (opts.gain ?? 1) * variantGain(b, variant);
    const cur = this.loops.get(id);
    if (cur && !cur.stopped && cur.key === key && cur.variant === variant) { this.setLoopGain(id, opts.gain ?? 1, opts.fadeMs ?? 1500); return; }
    if (cur) this.stopLoop(id, opts.fadeMs ?? 1500);
    const g = ctx.createGain();
    g.gain.value = 0;
    g.connect(this.buses.get(b.bus) ?? this.master!);
    const loop: Loop = { key, variant, gain: g, target, sources: [], stopped: false };
    this.loops.set(id, loop);
    void this.buffer(key, variant).then((buf) => {
      if (!buf || loop.stopped || !this.ctx) return;
      const fade = (opts.fadeMs ?? 1500) / 1000;
      g.gain.setTargetAtTime(target, this.ctx.currentTime, Math.max(0.01, fade / 3));
      // the bank's stretch of the master (a capped bed loops over its first max_ms), and the server
      // clock's place in it: every player hears the same stretch of rain
      const r = region(b.variants[variant]!, buf.duration);
      if (r.duration <= 0) return;
      const offset = ((serverNow() / 1000) % r.duration + r.duration) % r.duration;
      if (b.loop === 'seamless' || r.duration < 4) {
        const s = this.ctx.createBufferSource(); s.buffer = buf; s.loop = true; s.loopStart = r.offset; s.loopEnd = r.offset + r.duration;
        s.connect(g); s.start(this.ctx.currentTime, r.offset + offset);
        loop.sources.push(s);
      } else {
        this.crossfade(loop, buf, r, this.ctx.currentTime, offset, Math.min(b.crossfadeMs / 1000 || 2, r.duration / 4));
      }
    });
  }

  /** One pass of a crossfading loop from `offset` into its region, scheduling the next pass to fade in under its tail. */
  private crossfade(loop: Loop, buf: AudioBuffer, r: { offset: number; duration: number }, at: number, offset: number, xf: number): void {
    const ctx = this.ctx!;
    if (loop.stopped) return;
    const s = ctx.createBufferSource(); s.buffer = buf;
    const g = ctx.createGain(); s.connect(g); g.connect(loop.gain);
    const len = r.duration - offset;
    g.gain.setValueAtTime(offset > 0 || at > ctx.currentTime ? 0 : 1, at);
    g.gain.linearRampToValueAtTime(1, at + Math.min(xf, len / 2));
    g.gain.setValueAtTime(1, at + Math.max(xf, len - xf));
    g.gain.linearRampToValueAtTime(0, at + len);
    s.start(at, r.offset + offset);
    s.stop(at + len + 0.05);
    loop.sources.push(s);
    s.onended = () => { loop.sources = loop.sources.filter((x) => x !== s); try { g.disconnect(); } catch { /* gone */ } };
    const next = at + Math.max(xf, len - xf);
    loop.timer = setTimeout(() => this.crossfade(loop, buf, r, next, 0, xf), Math.max(0, (next - ctx.currentTime - 1) * 1000));
  }

  setLoopGain(id: string, gain: number, rampMs = 800): void {
    const l = this.loops.get(id);
    const b = l && this.bank(l.key);
    if (!l || !b || !this.ctx) return;
    l.target = gain * variantGain(b, l.variant);
    l.gain.gain.cancelScheduledValues(this.ctx.currentTime);
    l.gain.gain.setTargetAtTime(l.target, this.ctx.currentTime, Math.max(0.01, rampMs / 3000));
  }

  stopLoop(id: string, fadeMs = 1200): void {
    this.pendingFileLoops.delete(id);
    const l = this.loops.get(id);
    if (!l) return;
    this.loops.delete(id);
    l.stopped = true;
    if (l.timer) clearTimeout(l.timer);
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    l.gain.gain.cancelScheduledValues(t);
    l.gain.gain.setTargetAtTime(0, t, Math.max(0.01, fadeMs / 3000));
    for (const s of l.sources) { try { s.stop(t + fadeMs / 1000 + 0.1); } catch { /* stopped */ } }
  }

  /** Every loop id playing now. */
  loopIds(): string[] { return [...this.loops.keys()]; }

  stopAllLoops(fadeMs = 1200): void { for (const id of [...this.loops.keys()]) this.stopLoop(id, fadeMs); }

  // ---- music (docs/44) ---------------------------------------------------------------------------

  /**
   * Plays a track of a music bank, crossfading from the one playing. Idempotent for the track
   * already playing (its level is retuned). False when there is no such bank or no audio.
   */
  playMusic(key: string, opts: MusicOptions = {}): boolean {
    const b = this.bank(key);
    const ctx = this.context();
    if (!b || b.kind !== 'music' || !b.variants.length || !ctx || typeof Audio === 'undefined') return false;
    const n = b.variants.length;
    let variant = opts.variant;
    if (variant === undefined) {
      const last = this.lastTrack.get(key);
      variant = Math.floor(this.rand() * n);
      if (n > 1 && variant === last) variant = (variant + 1) % n;
    }
    variant = Math.max(0, Math.min(n - 1, variant));
    const fadeS = (opts.fadeMs ?? 2000) / 1000;
    const target = (opts.gain ?? 1) * variantGain(b, variant);
    const cur = this.track;
    if (cur && !cur.stopped && cur.key === key && cur.variant === variant) {
      cur.target = target;
      cur.el.loop = !!opts.loop;
      cur.gain.gain.setTargetAtTime(target, ctx.currentTime, Math.max(0.01, fadeS / 3));
      return true;
    }
    this.stopMusic(opts.fadeMs ?? 2000);
    this.lastTrack.set(key, variant);
    const el = new Audio();
    el.crossOrigin = 'anonymous';
    el.preload = 'auto';
    el.loop = !!opts.loop;
    const node = ctx.createMediaElementSource(el);
    const gain = ctx.createGain();
    gain.gain.value = 0;
    node.connect(gain);
    gain.connect(this.buses.get('music') ?? this.master!);
    const t: Track = { key, variant, el, node, gain, target, stopped: false, started: false };
    this.track = t;
    el.addEventListener('ended', () => {
      if (t.stopped || this.track !== t) return;
      this.release(t);
      this.track = undefined;
      for (const cb of [...this.musicEnded]) cb({ key, variant: t.variant });
    });
    this.record({ key, variant, gain: target, rate: 1, delayMs: 0, at: deviceNow(), origin: 'local' });
    const at = opts.startAt && opts.startAt > 0 ? `#t=${opts.startAt.toFixed(2)}` : '';
    const url = b.variants[variant]!.url;
    if (!this.musicSrc) { el.src = url + at; this.startTrack(t, fadeS); return true; }
    // a cached track plays from its local copy; otherwise it streams
    void this.musicSrc(url).catch(() => undefined).then((local) => {
      if (t.stopped) { if (local) URL.revokeObjectURL(local); return; }
      t.objectUrl = local;
      el.src = (local ?? url) + at;
      this.startTrack(t, fadeS);
    });
    return true;
  }

  /** Starts a track's element and fades it up (again after a gesture, if the browser refused). */
  private startTrack(t: Track, fadeS = 2): void {
    const ctx = this.ctx;
    if (!ctx || t.stopped) return;
    if (ctx.state !== 'running') return;   // flushUnlocked starts it on the first gesture
    if (!t.el.src) return;   // its source is still resolving; that starts it
    t.started = true;
    t.gain.gain.cancelScheduledValues(ctx.currentTime);
    t.gain.gain.setTargetAtTime(t.target, ctx.currentTime, Math.max(0.01, fadeS / 3));
    void t.el.play().catch(() => { t.started = false; });
  }

  /** Fades the score out and stops it. */
  stopMusic(fadeMs = 2000): void {
    const t = this.track;
    this.track = undefined;
    if (!t || t.stopped) return;
    t.stopped = true;
    const ctx = this.ctx;
    if (!ctx || !t.started) { this.release(t); return; }
    t.gain.gain.cancelScheduledValues(ctx.currentTime);
    t.gain.gain.setTargetAtTime(0, ctx.currentTime, Math.max(0.01, fadeMs / 3000));
    setTimeout(() => this.release(t), fadeMs + 200);
  }

  /** Lets go of a track's element (stops the download, frees the decoder). */
  private release(t: Track): void {
    t.stopped = true;
    try { t.el.pause(); t.el.removeAttribute('src'); t.el.load(); } catch { /* gone */ }
    try { t.node.disconnect(); t.gain.disconnect(); } catch { /* gone */ }
    if (t.objectUrl) { URL.revokeObjectURL(t.objectUrl); t.objectUrl = undefined; }
  }

  /** Lowers the score under a cinematic's words: 0 = full level, 1 = silent. */
  setMusicDuck(amount: number, rampMs = 600): void {
    this.duckAmount = Math.max(0, Math.min(1, amount));
    const ctx = this.ctx;
    if (!ctx || !this.duck) return;
    this.duck.gain.cancelScheduledValues(ctx.currentTime);
    this.duck.gain.setTargetAtTime(1 - this.duckAmount, ctx.currentTime, Math.max(0.01, rampMs / 3000));
  }

  /** The track playing (or fading up), if any. */
  musicNow(): { key: string; variant: number } | undefined {
    const t = this.track;
    return t && !t.stopped ? { key: t.key, variant: t.variant } : undefined;
  }

  /** Called when a (non-looping) track plays to its end; returns the unsubscribe. */
  onMusicEnded(cb: (t: { key: string; variant: number }) => void): () => void {
    this.musicEnded.add(cb);
    return () => { this.musicEnded.delete(cb); };
  }

  /** What the engine is doing, for tests and the debug hook. */
  info(): { state: string; voices: number; loops: Array<{ id: string; key: string; variant: number }>; music?: { key: string; variant: number }; decodedMB: number; fetched: number; banks: number } {
    return {
      state: this.ctx?.state ?? 'none',
      music: this.musicNow(),
      voices: this.voices.filter((v) => !v.stopped).length,
      loops: [...this.loops].map(([id, l]) => ({ id, key: l.key, variant: l.variant })),
      decodedMB: Math.round((this.decodedBytes / 1048576) * 10) / 10,
      fetched: this.bytes.size,
      banks: Object.keys(this.manifest.banks).length,
    };
  }
}

export { dbToGain };
